import css from "./contactinformation.scss?inline";
import globalStyles from "../../index.scss?inline";
import { Component } from "../../utils/Component";
import { ArgSpecDictionary } from "../component-utils";

const DEBUG_VERBOSE: boolean = false;
const CLASS_NAME: string = "HHDSContactInformation";
export const HHDSContactInformationTagName: string = "hhds-contact-information";
const TAG_NAME: string = HHDSContactInformationTagName;

export const HHDSContactInformationAttrNames = {
	phone: "phone",
	email: "email",
	sales_office: "sales_office",
	directions: "directions",
};

const Attrs = HHDSContactInformationAttrNames;

export class HHDSContactInformation extends Component {
	constructor() {
		super();
		// The base class's constructor handles attachmennt of a shadow root and
		// adopted global styles. Access the shadow root via this.shadow.
		//
		// Use the constructor only for anything that will never need to be destroyed as part of the
		// component's update lifecycle. init() and destroy() are called for connectedCallback and
		// disconnectedCallback, and a destroy() init() pair is called if reinit() is utilised.
	}

	protected override init(): void {
		// The base class responds to connectedCallback() by collecting attributes into
		// this.vars, then calling init(). A call to super.init() is not required.
		DEBUG_VERBOSE && console.log(CLASS_NAME, "init");

		const phone = this.vars.get<number>(Attrs.phone);
		const email = this.vars.get<number>(Attrs.email);
		const sales_office = this.vars.get<number>(Attrs.sales_office);
		const directions = this.vars.get<number>(Attrs.directions);

		// For some components, re-assigning innerHTML may be appropriate on attribute and slot changes,
		// and without the need for a reinit(). In other cases, assigning innerHTML explicitly as part of
		// the init() routine is more appropriate.
		this.shadow.innerHTML = `
      <style>${globalStyles}</style>
			<style>${css}</style>
			<div class="${TAG_NAME}">
        <ul>
          ${
						phone
							? `<li><p class="uppercase uppercase--01">Phone</p><p class="bodytext bodytext--01 bodytext--black-links"><a href="tel:${phone}">${phone}</a></p></li>`
							: ``
					}
          ${
						email
							? `<li><p class="uppercase uppercase--01">Email</p><p class="bodytext bodytext--01 bodytext--black-links"><a href="mailto:${email}">${email}</a></p></li>`
							: ``
					}
          ${
						sales_office
							? `<li><p class="uppercase uppercase--01">Sales office</p><p><address class="bodytext bodytext--01">${sales_office}</address></p></li>`
							: `<li><p class="uppercase uppercase--01">Sales office</p><p><address class="bodytext bodytext--01">Sales office coming soon</address></p></li>`
					}
          </ul>
          ${
						sales_office && directions
							? `<p class="${TAG_NAME}__directions"><a href="${directions}" target="_blank" class="bodytext bodytext--01"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Frame"><path id="Vector" d="M10 1.25C8.17727 1.25207 6.42979 1.97706 5.14092 3.26592C3.85206 4.55479 3.12707 6.30227 3.125 8.125C3.125 14.0078 9.375 18.4508 9.64141 18.6367C9.74649 18.7103 9.87169 18.7498 10 18.7498C10.1283 18.7498 10.2535 18.7103 10.3586 18.6367C10.625 18.4508 16.875 14.0078 16.875 8.125C16.8729 6.30227 16.1479 4.55479 14.8591 3.26592C13.5702 1.97706 11.8227 1.25207 10 1.25ZM10 5.625C10.4945 5.625 10.9778 5.77162 11.3889 6.04633C11.8 6.32103 12.1205 6.71148 12.3097 7.16829C12.4989 7.62511 12.5484 8.12777 12.452 8.61273C12.3555 9.09768 12.1174 9.54314 11.7678 9.89277C11.4181 10.2424 10.9727 10.4805 10.4877 10.577C10.0028 10.6734 9.50011 10.6239 9.04329 10.4347C8.58648 10.2455 8.19603 9.92505 7.92133 9.51393C7.64662 9.1028 7.5 8.61945 7.5 8.125C7.5 7.46196 7.76339 6.82607 8.23223 6.35723C8.70107 5.88839 9.33696 5.625 10 5.625Z" fill="#318171"/></g></svg><span>Get Directions</span></a></p>`
							: ``
					}
      </div>
		`;

		// If the component uses slots, use observeSlotChanges().
		// this.observeSlotChanges(true);
	}

	protected override destroy(): void {
		// The base class responds to disconnectedCallback() by collecting attributes into
		// this.vars, then calling destroy(). A call to super.destroy() is not required.
		DEBUG_VERBOSE && console.log(CLASS_NAME, "destroy");
		// If the component uses slots, stop observing slot changes.
		// this.observeSlotChanges(false);
	}

	override onAttributeChanged(
		name: string,
		_oldValue: string,
		newValue: string
	): void {
		DEBUG_VERBOSE &&
			console.log(CLASS_NAME, "Attribute changed: ", name, _oldValue, newValue);
		// Either call reinit() to have the component's destroy and init methods each be called,
		// or skip this step and handle update of the attribute directly. 'this.vars' will already
		// have been updated by the base Component class, so it can be immediately used to access
		// the new value.
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, elements: Element[]): void {
		if (elements.length == 0) {
			DEBUG_VERBOSE && console.log(CLASS_NAME, "Slot emptied");
		} else {
			DEBUG_VERBOSE && console.log(CLASS_NAME, "Slot changed");
		}
	}

	static override argSpecs(): ArgSpecDictionary {
		// The base Component class must have access to this superclass's ArgSpecs.
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.phone]: {
		description: "Add phone number",
		defaultValue: "",
		type: String,
	},
	[Attrs.email]: {
		description: "Add email",
		defaultValue: "",
		type: String,
	},
	[Attrs.sales_office]: {
		description: "Add sales office",
		defaultValue: "",
		type: String,
	},
	[Attrs.directions]: {
		description: "Add directions",
		defaultValue: "",
		type: String,
	},
};
